import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { CalendarIcon, ImagePlus, X, Eye, Check, ChevronsUpDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useGrowthSquadMembers } from "@/hooks/useGrowthSquadMembers";
import { useCreateGrowthTask } from "@/hooks/useCreateGrowthTask";
import { ProjectSelector } from "@/components/projects/ProjectSelector";
import { useSprints } from "@/hooks/useSprints";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Copy } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTaskCreationSettings } from "@/hooks/useTaskCreationSettings";
import { useUserGoogleCalendar } from "@/hooks/useUserGoogleCalendar";
import { TimeTracker } from "@/components/ui/time-tracker";
import { ContentSourceSelector, SourceType, SelectedDocHubItems } from "@/components/content-source-selector";
import { logTimeEntry, TimeTrackingSource } from "@/hooks/useTimeTracking";
import { useDocHubTips } from "@/hooks/useDocHubTips";
import { useDocHubFiles } from "@/hooks/useDocHubFiles";
import { useOperationsMoms } from "@/hooks/useOperationsMoms";
import { containsVerb } from "@/utils/verbDetection";

interface QuickAddTaskProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSprintId?: string | null;
}

const QuickAddTask = ({ 
  open, 
  onOpenChange,
  currentSprintId
}: QuickAddTaskProps) => {
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [difficulty, setDifficulty] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [assignedTo, setAssignedTo] = useState<string>("");
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [watchers, setWatchers] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<Date>();
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const isMobile = useIsMobile();
  
  // Estimated Time state
  const [timeEstimated, setTimeEstimated] = useState<number>(0);
  
  // Attachments state
  const [attachmentSourceType, setAttachmentSourceType] = useState<SourceType>('url');
  const [attachmentUrl, setAttachmentUrl] = useState<string>('');
  const [attachmentText, setAttachmentText] = useState<string>('');
  const [attachmentFile, setAttachmentFile] = useState<globalThis.File | null>(null);
  const [selectedDocHubItems, setSelectedDocHubItems] = useState<SelectedDocHubItems>({
    tips: [],
    files: [],
    moms: []
  });
  
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const { brands, currentBrandId: managerBrandId } = useBrandManager();
  const currentBrand = brands.find(brand => brand.id === currentBrandId);
  const { members } = useGrowthSquadMembers();
  const { createTask, isCreating } = useCreateGrowthTask();
  const { sprints } = useSprints();
  const { toast } = useToast();
  const { user } = useAuth();
  const { settings: taskCreationSettings } = useTaskCreationSettings();
  const { createEvent: createCalendarEvent } = useUserGoogleCalendar();
  
  // Fetch Doc Hub data
  const { tips: docHubTips } = useDocHubTips(currentBrandId || '');
  const { files: docHubFiles } = useDocHubFiles(currentBrandId || '');
  const { moms: docHubMoms } = useOperationsMoms(currentBrandId);

  // Find current sprint
  const currentSprint = sprints.find(sprint => {
    const now = new Date();
    const startDate = new Date(sprint.start_date);
    const endDate = new Date(sprint.end_date);
    return now >= startDate && now <= endDate && sprint.status === 'open';
  }) || sprints.find(sprint => sprint.status === 'open');

  // Sync brand from useBrandManager (already resolved, no extra fetch needed)
  useEffect(() => {
    if (managerBrandId) {
      setCurrentBrandId(managerBrandId);
    }
  }, [managerBrandId]);

  // Listen to brand change events
  useEffect(() => {
    const handleBrandChange = (event: CustomEvent) => {
      const { brandId } = event.detail;
      setCurrentBrandId(brandId);
    };

    window.addEventListener('brandChanged', handleBrandChange as EventListener);
    return () => {
      window.removeEventListener('brandChanged', handleBrandChange as EventListener);
    };
  }, []);

// Searchable inline dropdown for Assigned To (avoids focus trap issues in Dialog)
function AssignedToSelector({ members, currentUser, value, onValueChange }: {
  members: { id: string; name: string; alias?: string }[];
  currentUser?: { id: string; name: string } | null;
  value: string;
  onValueChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = members.find(m => m.id === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="space-y-2">
      <Label htmlFor="quick-assigned-to">Assigned To</Label>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          )}
        >
          <span className={selected ? "" : "text-muted-foreground"}>
            {selected ? (selected.name === currentUser?.name ? 'Me' : selected.name) : "Select member"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
        {open && (
          <div className="absolute top-full left-0 right-0 z-[9999] mt-1 rounded-md border bg-popover shadow-md">
            <Command>
              <CommandInput placeholder="Search members..." autoFocus />
              <CommandList className="max-h-[200px]">
                <CommandEmpty>No members found.</CommandEmpty>
                <CommandGroup>
                  {[...members].sort((a, b) => {
                    const lastNameA = a.name.trim().split(/\s+/).slice(-1)[0] || '';
                    const lastNameB = b.name.trim().split(/\s+/).slice(-1)[0] || '';
                    return lastNameA.localeCompare(lastNameB) || a.name.localeCompare(b.name);
                  }).map((member) => (
                    <CommandItem
                      key={member.id}
                      value={member.name}
                      onSelect={() => {
                        onValueChange(member.id);
                        setOpen(false);
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value === member.id ? "opacity-100" : "opacity-0")} />
                      <span>{member.name === currentUser?.name ? 'Me' : member.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>
    </div>
  );
}


  // Image upload functions
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...imageFiles].slice(0, 3)); // Max 3 images
    }
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (): Promise<string[]> => {
    if (selectedImages.length === 0) return [];
    
    setUploadingImages(true);
    const uploadedUrls: string[] = [];

    try {
      for (const image of selectedImages) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `task-images/${currentBrand?.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('task-images')
          .upload(filePath, image);

        if (uploadError) {
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('task-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setUploadingImages(false);
    }

    return uploadedUrls;
  };

  // Reset all form state when brand changes
  useEffect(() => {
    setTaskTitle("");
    setTaskDescription("");
    setPriority('Medium');
    setDifficulty('Medium');
    setAssignedTo("");
    setWatchers([]);
    setDueDate(undefined);
    setSelectedImages([]);
    setIsInternal(false);
    setTimeEstimated(0);
    setAttachmentSourceType('url');
    setAttachmentUrl('');
    setAttachmentText('');
    setAttachmentFile(null);
    setSelectedDocHubItems({ tips: [], files: [], moms: [] });
    setIsDatePickerOpen(false);
    setProjectId(undefined);
  }, [currentBrand?.id]);

  // Reset date picker state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsDatePickerOpen(false);
    }
  }, [open]);

  // Handlers for time and attachments
  const handleTimeEstimateChange = (minutes: number, source: TimeTrackingSource) => {
    setTimeEstimated(minutes);
  };

  const handleToggleTip = (tipId: string) => {
    setSelectedDocHubItems(prev => ({
      ...prev,
      tips: prev.tips.includes(tipId) 
        ? prev.tips.filter(id => id !== tipId)
        : [...prev.tips, tipId]
    }));
  };

  const handleToggleFile = (fileId: string) => {
    setSelectedDocHubItems(prev => ({
      ...prev,
      files: prev.files.includes(fileId)
        ? prev.files.filter(id => id !== fileId)
        : [...prev.files, fileId]
    }));
  };

  const handleToggleMom = (momId: string) => {
    setSelectedDocHubItems(prev => ({
      ...prev,
      moms: prev.moms.includes(momId)
        ? prev.moms.filter(id => id !== momId)
        : [...prev.moms, momId]
    }));
  };

  // Build attachments array from ALL tabs (not just active tab)
  const buildAttachments = async (): Promise<any[]> => {
    const attachments: any[] = [];

    // Always check URL content (regardless of current tab)
    if (attachmentUrl.trim()) {
      attachments.push({ type: 'url', value: attachmentUrl.trim(), name: attachmentUrl.trim() });
    }

    // Always check text content
    if (attachmentText.trim()) {
      attachments.push({ type: 'text', value: attachmentText.trim(), name: 'Text note' });
    }

    // Always check file content
    if (attachmentFile) {
      const fileExt = attachmentFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `task-attachments/${currentBrand?.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('task-images')
        .upload(filePath, attachmentFile);

      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage
          .from('task-images')
          .getPublicUrl(filePath);
        attachments.push({ type: 'file', value: publicUrl, name: attachmentFile.name });
      }
    }

    // Always check DocHub selections
    if (selectedDocHubItems.tips.length > 0 || selectedDocHubItems.files.length > 0 || selectedDocHubItems.moms.length > 0) {
      selectedDocHubItems.tips.forEach(id => attachments.push({ type: 'dochub_tip', value: id, name: `Tip: ${id}` }));
      selectedDocHubItems.files.forEach(id => attachments.push({ type: 'dochub_file', value: id, name: `File: ${id}` }));
      selectedDocHubItems.moms.forEach(id => attachments.push({ type: 'dochub_mom', value: id, name: `MOM: ${id}` }));
    }

    return attachments;
  };

  // Find current user in squad members and set as default
  const currentUser = members.find(member => member.user_id === user?.id);
  
  // Set default assigned to current user when dialog opens and members are loaded
  useEffect(() => {
    if (open && currentUser?.id && !assignedTo && members.length > 0) {
      setAssignedTo(currentUser.id);
    }
  }, [open, currentUser?.id, assignedTo, members.length, currentBrand?.id]);

  const copyToClipboard = async (taskData: any) => {
    let taskUrl;
    
    // Get brand from useBrandManager (which includes slug)
    const brandWithSlug = brands.find(brand => brand.id === currentBrand?.id);
    
    // Determine link type from settings (default to friendly)
    const linkType = taskCreationSettings?.copy_link_type ?? 'friendly';
    
    // Use friendly URL if setting is 'friendly' and we have brand slug and task slug
    if (linkType === 'friendly' && (brandWithSlug as any)?.slug && taskData?.slug) {
      taskUrl = `${window.location.origin}/task/${(brandWithSlug as any).slug}/${taskData.slug}`;
    } else {
      // Use technical URL
      taskUrl = `${window.location.origin}/tasks/${currentBrand?.id}/${taskData?.id}`;
    }
    
    try {
      await navigator.clipboard.writeText(taskUrl);
      toast({
        title: "Success",
        description: "Task link copied to clipboard!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy task link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim() || !currentBrand?.id || !assignedTo) {
      return;
    }

    // Validate due date is required if setting is enabled
    if (taskCreationSettings?.due_date_required && !dueDate) {
      toast({
        title: "Due date required",
        description: "Please select a due date before creating this task",
        variant: "destructive",
      });
      return;
    }

    // Validate estimated time is required if setting is enabled
    if (taskCreationSettings?.estimated_time_mandatory && (!timeEstimated || timeEstimated === 0)) {
      toast({
        title: "Estimated time required",
        description: "Please set an estimated time for this task",
        variant: "destructive",
      });
      return;
    }

    // Validate verb in title if setting is enabled
    if (taskCreationSettings?.require_verb_in_title && !containsVerb(taskTitle)) {
      toast({
        title: "Action verb required",
        description: "Task title must include a verb to describe the action needed",
        variant: "destructive",
      });
      return;
    }

    try {
      // Upload images first
      const uploadedImageUrls = await uploadImages();
      
      // Build attachments
      const processedAttachments = await buildAttachments();

      const taskData = await createTask({
        title: taskTitle,
        description: taskDescription,
        priority: priority,
        difficulty: difficulty,
        assigned_to: assignedTo,
        brand_id: currentBrand.id,
        project_id: projectId,
        source_type: 'my_own_initiative',
        due_date: dueDate,
        images: uploadedImageUrls,
        watchers: watchers,
        source_data: {
          task_type: 'quick_add',
          sprint_id: currentSprintId
        },
        internal: isInternal,
        time_estimated: timeEstimated,
        attachments: processedAttachments
      });

      // Log time estimated to universal time tracking if set
      if (timeEstimated > 0 && taskData?.id) {
        await logTimeEntry({
          brandId: currentBrand.id,
          taskId: taskData.id,
          taskType: 'foundational',
          entryType: 'time_estimated',
          minutesAdded: timeEstimated,
          previousTotal: 0,
          newTotal: timeEstimated,
          source: 'manual',
          notes: 'Set during task creation'
        });
      }

      // Reset form
      setTaskTitle("");
      setTaskDescription("");
      setPriority('Medium');
      setDifficulty('Medium');
      setAssignedTo("");
      setWatchers([]);
      setDueDate(undefined);
      setSelectedImages([]);
      setIsInternal(false);
      setTimeEstimated(0);
      setAttachmentSourceType('url');
      setAttachmentUrl('');
      setAttachmentText('');
      setAttachmentFile(null);
      setSelectedDocHubItems({ tips: [], files: [], moms: [] });
      
      onOpenChange(false);

      // Copy task link to clipboard (if enabled in settings)
      if (taskData && (taskCreationSettings?.copy_link_on_submission !== false)) {
        await copyToClipboard(taskData);
      }

      // Sync to Google Calendar when toggle is on: create event with title, description (notes), due date (default today), estimated time
      if (taskData && (taskCreationSettings?.sync_with_google_calendar_on_tasks === true) && createCalendarEvent) {
        const eventDate = dueDate ? new Date(dueDate) : new Date();
        eventDate.setHours(9, 0, 0, 0);
        const start = new Date(eventDate);
        const durationMinutes = timeEstimated && timeEstimated > 0 ? timeEstimated : 60;
        const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
        const calendarResult = await createCalendarEvent(
          taskTitle.trim(),
          start.toISOString(),
          end.toISOString(),
          taskDescription?.trim() ?? ""
        );
        if (calendarResult?.eventId && taskData.id) {
          await supabase
            .from("foundational_tasks")
            .update({ google_calendar_event_id: calendarResult.eventId })
            .eq("id", taskData.id);
        }
      }
    } catch (error) {
      // Error handled by the createTask hook
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleCreateTask();
    }
  };

  // Lock body scroll on mobile while dialog is open, restore on close
  useEffect(() => {
    if (open && isMobile) {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${y}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        const scrollY = parseInt(document.body.style.top || '0', 10) * -1;
        
        // Temporarily block pointer events during transition
        document.body.style.pointerEvents = 'none';
        
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Defer scroll restoration to avoid conflicting with immediate user interactions
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY || 0);
            // Re-enable pointer events after scroll restoration
            setTimeout(() => {
              document.body.style.pointerEvents = '';
            }, 50);
          });
        });
      };
    }

    // If not open or not mobile, ensure styles are cleared
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
  }, [open, isMobile]);

  return (
    <Dialog key={currentBrand?.id} open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent 
        key={currentBrand?.id} 
        className={cn(
          "max-w-full w-full md:max-w-lg md:h-auto md:max-h-[90vh] overflow-y-auto md:px-8 [&>button]:hidden flex items-start justify-center md:block",
          isMobile ? "h-[80svh] max-h-[80svh] p-0" : "h-screen max-h-screen"
        )} 
        onKeyDown={handleKeyDown}
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking on popover content
          const target = e.target as HTMLElement;
          if (
            target.closest('[data-radix-popper-content-wrapper]') || 
            target.closest('[data-radix-popover-content]')
          ) {
            e.preventDefault();
          }
        }}
        onFocusOutside={(e) => {
          // Prevent focus trap from blocking popover interactions
          const target = e.target as HTMLElement;
          if (
            target.closest('[data-radix-popper-content-wrapper]') || 
            target.closest('[data-radix-popover-content]')
          ) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          // Prevent closing when interacting with popovers or when popover is open
          const target = e.target as HTMLElement;
          if (
            isDatePickerOpen ||
            target.closest('[data-radix-popper-content-wrapper]') || 
            target.closest('[data-radix-popover-content]') ||
            target.closest('[data-radix-popover-trigger]')
          ) {
            e.preventDefault();
          }
        }}
      >
        <div className="w-full max-w-lg md:max-w-none relative">
        {/* Action Buttons - Top Right Corner */}
        <div className={cn(
          "absolute z-50 flex",
          isMobile ? "top-0 right-0" : "-top-6 -right-8"
        )}>
          <div className="px-2 py-1.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-5 w-6 text-red-500 hover:text-red-600 hover:bg-red-50 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="bg-green-500 px-2 py-1.5 rounded-bl-[1.5rem]">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleCreateTask}
              disabled={!taskTitle.trim() || !currentBrand?.id || !assignedTo || isCreating || uploadingImages}
              className="h-5 w-6 text-white hover:bg-green-600 hover:text-white p-0"
            >
              <Check className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <DialogHeader className={cn(
          isMobile && "sticky top-0 z-10 bg-background border-b px-6 py-4"
        )}>
          <div className="flex items-center justify-between gap-4 pr-32">
          <div className="flex items-center gap-4">
            <DialogTitleWithBug 
              className="flex items-center gap-2 whitespace-nowrap"
              popupName="Quick Add Task"
              popupContext="/dashboard"
              isOpen={open}
            >
              Quick Add Task
            </DialogTitleWithBug>
              
            {/* Internal Task Checkbox - only show if setting enabled */}
            {taskCreationSettings?.internal_task_visible !== false && (
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="internal-task-header"
                  checked={isInternal}
                  onCheckedChange={(checked) => setIsInternal(checked === true)}
                />
                <Label htmlFor="internal-task-header" className="text-sm cursor-pointer whitespace-nowrap">
                  Internal?
                </Label>
              </div>
            )}
          </div>
          </div>
          {currentBrand && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground hidden">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span>Brand: {currentBrand.name}</span>
            </div>
          )}
        </DialogHeader>
        
        <div className={cn(
          "space-y-4",
          isMobile && "px-6 py-4"
        )}>
          <div className="space-y-2">
            <Label htmlFor="quick-task-title" className="hidden">Task Title *</Label>
            <Input
              id="quick-task-title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              placeholder="Enter task title *"
              autoFocus={!isMobile}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quick-task-description" className="hidden">Task Description</Label>
            <Textarea
              id="quick-task-description"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Detailed description of what needs to be done..."
              rows={3}
            />
          </div>

          {/* Image Upload and Due Date Row */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {/* Image Upload */}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
                id="image-upload"
                disabled={selectedImages.length >= 3}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('image-upload')?.click()}
                disabled={selectedImages.length >= 3}
                className="flex items-center gap-2"
              >
                <ImagePlus className="h-4 w-4" />
                Images ({selectedImages.length}/3)
              </Button>
              
              {/* Due Date */}
              <Popover modal={false} open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !dueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "PPP") : <span>Pick a due date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-auto p-0" 
                  align="start"
                  onOpenAutoFocus={(e) => {
                    // Prevent auto-focus on mobile to avoid keyboard issues
                    if (isMobile) {
                      e.preventDefault();
                    }
                  }}
                  onInteractOutside={(e) => {
                    // Prevent closing when clicking inside the dialog
                    const target = e.target as HTMLElement;
                    const dialog = target.closest('[role="dialog"]');
                    const isInsideDialog = dialog !== null;
                    
                    // Only allow closing if clicking outside both dialog and popover
                    if (isInsideDialog) {
                      e.preventDefault();
                    }
                  }}
                  onPointerDownOutside={(e) => {
                    // Prevent closing when clicking inside the dialog
                    const target = e.target as HTMLElement;
                    const dialog = target.closest('[role="dialog"]');
                    if (dialog) {
                      e.preventDefault();
                    }
                  }}
                  onEscapeKeyDown={(e) => {
                    // Allow escape to close
                    setIsDatePickerOpen(false);
                  }}
                  style={{
                    // Ensure popover is above dialog on mobile
                    zIndex: 99999,
                  }}
                >
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={(date) => {
                      setDueDate(date);
                      // Close popover after selecting a date (both mobile and desktop)
                      if (date) {
                        setIsDatePickerOpen(false);
                      }
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      
                      // Check same-day restriction
                      if (taskCreationSettings?.allow_same_day_tasks === false) {
                        const dateToCheck = new Date(date);
                        dateToCheck.setHours(0, 0, 0, 0);
                        if (dateToCheck.getTime() === today.getTime()) {
                          return true; // Disable today
                        }
                      }
                      
                      // Check sprint date restriction
                      if (taskCreationSettings?.allow_off_sprint_dates === false && currentSprint) {
                        const sprintStart = new Date(currentSprint.start_date);
                        const sprintEnd = new Date(currentSprint.end_date);
                        return date < sprintStart || date > sprintEnd;
                      }
                      
                      // Default: only prevent past dates
                      return date < today;
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Selected Images Preview */}
            {selectedImages.length > 0 && (
              <div className="flex gap-2 mt-2">
                {selectedImages.map((file, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      className="w-16 h-16 object-cover rounded-md border"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {taskCreationSettings?.project_enabled !== false && (
            <div className="space-y-2">
              <Label htmlFor="quick-project">Project (Optional)</Label>
              <ProjectSelector
                value={projectId}
                onValueChange={setProjectId}
                brandId={currentBrandId || undefined}
                placeholder="Select a project"
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="quick-priority">Priority</Label>
              <Select value={priority} onValueChange={(value: 'High' | 'Medium' | 'Low') => setPriority(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-difficulty">Difficulty</Label>
              <Select value={difficulty} onValueChange={(value: 'High' | 'Medium' | 'Low') => setDifficulty(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <AssignedToSelector
              members={members}
              currentUser={currentUser}
              value={assignedTo}
              onValueChange={setAssignedTo}
            />
          </div>

          {/* Watchers Section */}
          <div className="space-y-2">
            <Label>
              People to notify (Watchers)
            </Label>
            <div className="grid grid-cols-7 gap-2">
              {members.map((member) => {
                const isSelected = watchers.includes(member.id);
                const displayText = member.alias || (() => {
                  const nameParts = member.name.split(' ');
                  return nameParts.length > 1
                    ? nameParts.slice(0, 2).map(n => n[0]).join('').toUpperCase()
                    : member.name.slice(0, 2).toUpperCase();
                })();
                
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setWatchers(prev => prev.filter(id => id !== member.id));
                      } else {
                        setWatchers(prev => [...prev, member.id]);
                      }
                    }}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium transition-all",
                      isSelected 
                        ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2" 
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                    title={`${member.name}${member.alias ? ` (${member.alias})` : ''}`}
                  >
                    {displayText}
                  </button>
                );
              })}
            </div>
            {watchers.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {watchers.length} watcher{watchers.length > 1 ? 's' : ''} selected
              </div>
            )}
          </div>



          {/* Estimated Time and Attachments Section */}
          <div className="space-y-4">
            {/* Estimated Time */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">How much time you estimate it will take?</Label>
              <TimeTracker 
                value={timeEstimated}
                onChange={handleTimeEstimateChange}
              />
            </div>
            
            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Attachments</Label>
              <ContentSourceSelector
                sourceType={attachmentSourceType}
                onSourceTypeChange={setAttachmentSourceType}
                textInput={attachmentText}
                onTextInputChange={setAttachmentText}
                textLabel="Notes"
                textPlaceholder="Add notes or references..."
                urlInput={attachmentUrl}
                onUrlInputChange={setAttachmentUrl}
                urlLabel="Link URL"
                urlPlaceholder="https://..."
                urlHelpText="Add a reference link"
                fileInput={attachmentFile}
                onFileInputChange={setAttachmentFile}
                fileLabel="Attach File"
                fileAccept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                fileHelpText="Upload a document or image"
                showDocHub={!!currentBrand}
                currentBrand={currentBrand}
                brandId={currentBrand?.id}
                selectedDocHubItems={selectedDocHubItems}
                onToggleTip={handleToggleTip}
                onToggleFile={handleToggleFile}
                onToggleMom={handleToggleMom}
                tips={docHubTips}
                files={docHubFiles}
                moms={docHubMoms}
              />
            </div>
          </div>

        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddTask;